import { c as _c } from "react-compiler-runtime";
import { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { buildReactCompilerMetrics } from '../react-performance-metrics';
import { logMetric } from '../debug';
/**
 * Tracks component render duration and emits compiler-aware metrics.
 * 100% sampling in all environments. If future gating is needed, guard this hook behind a feature flag.
 */
export function useCompilerRenderMetrics(componentName, props, tracker, state, opts) {
  var _opts$compilerOptimiz;
  const $ = _c(21);
  let t0;
  if ($[0] !== tracker) {
    t0 = () => buildReactCompilerMetrics(tracker);
    $[0] = tracker;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [ReactCompilerMetrics] = useState(t0);
  const [perfNow] = useState(_temp);
  const startRef = useRef(perfNow);
  const renderReason = useRenderReasonTracker(props, state);
  const isReactCompilerOptimized = (_opts$compilerOptimiz = opts === null || opts === void 0 ? void 0 : opts.compilerOptimized) !== null && _opts$compilerOptimiz !== void 0 ? _opts$compilerOptimiz : true;
  let t1;
  if ($[2] !== ReactCompilerMetrics || $[3] !== componentName || $[4] !== isReactCompilerOptimized || $[5] !== (opts === null || opts === void 0 ? void 0 : opts.childrenCount) || $[6] !== (opts === null || opts === void 0 ? void 0 : opts.propsCount) || $[7] !== (opts === null || opts === void 0 ? void 0 : opts.routeName) || $[8] !== (opts === null || opts === void 0 ? void 0 : opts.stateCount) || $[9] !== renderReason) {
    t1 = () => {
      const renderDuration = performance.now() - startRef.current;
      ReactCompilerMetrics.trackComponentRender({
        componentName,
        routeName: opts === null || opts === void 0 ? void 0 : opts.routeName,
        propsCount: opts === null || opts === void 0 ? void 0 : opts.propsCount,
        stateCount: opts === null || opts === void 0 ? void 0 : opts.stateCount,
        childrenCount: opts === null || opts === void 0 ? void 0 : opts.childrenCount
      }, {
        renderDuration,
        isReactCompilerOptimized,
        renderReason
      });
      logMetric("component-render-duration", {
        component: componentName,
        route: opts === null || opts === void 0 ? void 0 : opts.routeName,
        compiler_optimized: isReactCompilerOptimized.toString(),
        render_reason: renderReason
      }, renderDuration);
      startRef.current = performance.now();
    };
    $[2] = ReactCompilerMetrics;
    $[3] = componentName;
    $[4] = isReactCompilerOptimized;
    $[5] = opts === null || opts === void 0 ? void 0 : opts.childrenCount;
    $[6] = opts === null || opts === void 0 ? void 0 : opts.propsCount;
    $[7] = opts === null || opts === void 0 ? void 0 : opts.routeName;
    $[8] = opts === null || opts === void 0 ? void 0 : opts.stateCount;
    $[9] = renderReason;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  useLayoutEffect(t1);
  let t2;
  if ($[11] !== ReactCompilerMetrics) {
    t2 = ReactCompilerMetrics.getRenderCount();
    $[11] = ReactCompilerMetrics;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== ReactCompilerMetrics) {
    t3 = ReactCompilerMetrics.getAvgRenderTime();
    $[13] = ReactCompilerMetrics;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== ReactCompilerMetrics) {
    t4 = ReactCompilerMetrics.getRenderReason();
    $[15] = ReactCompilerMetrics;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = {
      renderCount: t2,
      avgRenderTime: t3,
      renderReason: t4
    };
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}

/**
 * Hook to automatically detect why a component re-rendered
 * Tracks changes in props and state to determine render causes
 */
function _temp() {
  return performance.now();
}
export function useRenderReasonTracker(props, state) {
  const $ = _c(7);
  const prevPropsRef = useRef(undefined);
  const prevStateRef = useRef(undefined);
  const renderCountRef = useRef(0);
  const [renderReason, setRenderReason] = useState(_temp2);
  let t0;
  let t1;
  if ($[0] !== props || $[1] !== state) {
    t0 = () => {
      renderCountRef.current = renderCountRef.current + 1;
      const isFirstRender = renderCountRef.current === 1;
      const getRenderReason = () => {
        if (isFirstRender) {
          return "first";
        }
        const propsChanged = prevPropsRef.current && !shallowEqual(prevPropsRef.current, props);
        const stateChanged = state && prevStateRef.current && !shallowEqual(prevStateRef.current, state);
        if (stateChanged) {
          return "state";
        } else {
          if (propsChanged) {
            return "props";
          } else {
            return "parent";
          }
        }
      };
      setRenderReason(getRenderReason());
    };
    t1 = [props, state];
    $[0] = props;
    $[1] = state;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== props || $[5] !== state) {
    t2 = () => {
      prevPropsRef.current = props;
      if (state) {
        prevStateRef.current = state;
      }
    };
    $[4] = props;
    $[5] = state;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useLayoutEffect(t2);
  return renderReason;
}

/**
 * Shallow equality check for objects
 */
function _temp2() {
  return "first";
}
export function shallowEqual(objA, objB) {
  if (objA === objB) {
    return true;
  }
  if (!objA || !objB) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if (!(key in objB) || objA[key] !== objB[key]) {
      return false;
    }
  }
  return true;
}

/**
 * Combined hook that tracks both render performance and automatically detects render reasons
 * This is the recommended hook for most components
 */
export function useCompilerRenderMetricsWithReasonTracking(componentName, props, tracker, state, options) {
  return useCompilerRenderMetrics(componentName, props, tracker, state, options);
}

/**
 * Example usage in a component:
 *
 * OLD WAY (manual render reason):
 * ```tsx
 * const MyComponent = ({ prop1, prop2 }) => {
 *   useCompilerRenderMetrics('MyComponent', {
 *     routeName: 'my-route',
 *     compilerOptimized: true,
 *     renderReason: 'props', // Manually specified
 *     propsCount: 2,
 *   });
 *
 *   return <div>{prop1} {prop2}</div>;
 * };
 * ```
 *
 * NEW WAY (automatic render reason detection):
 * ```tsx
 * const MyComponent = ({ prop1, prop2 }) => {
 *   const [state, setState] = useState(0);
 *
 *   useCompilerRenderMetricsWithReasonTracking(
 *     'MyComponent',
 *     { prop1, prop2 }, // Pass all props
 *     { state },        // Pass state if you want to track state changes
 *     {
 *       routeName: 'my-route',
 *       compilerOptimized: true,
 *       propsCount: 2,
 *     }
 *   );
 *
 *   return <div>{prop1} {prop2} {state}</div>;
 * };
 * ```
 *
 * Or use individual hooks:
 * ```tsx
 * const MyComponent = ({ prop1, prop2 }) => {
 *   const [state, setState] = useState(0);
 *
 *   // Just track render reasons
 *   const renderReason = useRenderReasonTracker(
 *     'MyComponent',
 *     { prop1, prop2 },
 *     { state }
 *   );
 *
 *   // Use render reason in your metrics
 *   useCompilerRenderMetrics('MyComponent', {
 *     renderReason, // Automatically detected!
 *     routeName: 'my-route',
 *     compilerOptimized: true,
 *   });
 *
 *   return <div>{prop1} {prop2} {state}</div>;
 * };
 * ```
 */

export default useCompilerRenderMetrics;